import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { evidenceBadgeClasses } from '../../data/evidence-presentation';
import {
  costModelSummary,
  organizationCostLabel,
  showSupporterExtra,
  usesRevenueShare,
  supporterExtraLabel,
} from '../../data/platform-result-presentation';
import type {
  PlatformId,
  PlatformOverviewViewModel,
} from '../../models/fundraising-comparison.models';

@Component({
  standalone: true,
  selector: 'app-platform-overview-card',
  imports: [CommonModule],
  templateUrl: './platform-overview-card.component.html',
  host: { class: 'd-block h-100' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlatformOverviewCardComponent {
  @Input({ required: true }) row!: PlatformOverviewViewModel;
  @Output() quickViewRequested = new EventEmitter<PlatformId>();
  @Output() detailLinkClicked = new EventEmitter<PlatformId>();

  evidenceBadgeClasses = evidenceBadgeClasses;

  get result() {
    return this.row.result;
  }

  get costModelSummary(): string {
    return costModelSummary(this.result);
  }

  get showSupporterExtra(): boolean {
    return showSupporterExtra(this.result);
  }

  get organizationCostLabel(): string {
    return organizationCostLabel(this.result);
  }

  get usesRevenueShare(): boolean {
    return usesRevenueShare(this.result);
  }

  get organizationRetainedPercentage(): string {
    return `${Number(
      (this.result.organizationRetainedBasisPoints / 100).toFixed(2),
    )}%`;
  }

  get supporterExtraLabel(): string {
    return supporterExtraLabel(this.result);
  }

  get supporterExtraValue(): string {
    return this.result.donorPaidExtraEstimated
      ? this.formatMoney(this.result.donorPaidExtraCents)
      : 'Optional — not estimated';
  }

  get accessibleSummary(): string {
    const parts = [
      this.result.platformName,
      this.costModelSummary,
      `Organization receives ${this.formatMoney(
        this.row.organizationReceivesCents,
      )}`,
      `${this.usesRevenueShare ? this.organizationCostLabel : 'Organization pays'} ${this.formatMoney(
        this.result.organizationCostCents,
      )}`,
    ];
    if (this.showSupporterExtra) {
      parts.push(`${this.supporterExtraLabel} ${this.supporterExtraValue}`);
    }
    return `${parts.join('. ')}.`;
  }

  formatMoney(amountCents: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: amountCents % 100 === 0 ? 0 : 2,
    }).format(amountCents / 100);
  }
}
